/**
 * The `structures` repository: the scan document, LIVE, with the two rules
 * the architecture assertion leaves out and the ghost-objects series
 * (#2657) learned the hard way:
 *
 *   1. Writes STATE OPERATIONS on named elements (`applyOps`), never an array
 *      from a client copy. The adapter's transaction applies them to what the
 *      server holds now, and an element the server no longer has is skipped.
 *   2. Subscriptions carry PENDING-FIELD semantics: while an `applyOps` on a
 *      field is in flight, a snapshot's value for that field is ignored and
 *      the optimistic local one kept; every other field is taken. The room
 *      store (`hooks/useRoomsSync`) does this per room; this does it per
 *      field on one document.
 *
 * And one rule from the performance section of the plan: a snapshot that
 * changes nothing hands back the SAME object, field by field, so nothing
 * downstream re-runs for a write that moved no data.
 *
 * `openStructure(id)` is a session: one listener on the document, a held
 * copy, the pending counts, and `applyOps`. The page that owns the viewer
 * opens one and folds what it emits into the `structureData` prop with
 * `foldStructureDocIntoAssembled` — which is how Step A of the element-store
 * plan makes the prop live without touching the hosts below it.
 *
 * Pure parts (`foldStructureSnapshot`, `foldStructureDocIntoAssembled`) are
 * exported for tests and own no Firebase; the only Firebase in this file is
 * through the adapter.
 */
import {
  applyStructureWrite as applyStructureWriteOnServer,
  readStructure,
  resolvePatch,
  watchStructure,
  writeKeys,
} from '../firebase/adapters/structures.adapter';
import { setPath } from './documentPaths';
import { createWriteQueue } from './documentWriteQueue';
import { applyOpeningOps } from '../../utils/openingArrayOps';
import { STRUCTURE_ARRAY_KEYS } from '../schemas/structure.schema';
import { sameStoredValue } from '@shared-lib/rooms/sameStoredValue';
import type {
  ApplyOpsResult,
  StructureArrayKey,
  StructureDoc,
  StructureOpsByKey,
  StructureWrite,
} from '../types/structure.types';

/**
 * Same content, by value. Cheap when the reference is shared. Not plain
 * `JSON.stringify` equality: Firestore hands a field back with its keys in a
 * different order, and a snapshot carrying nothing new would give the prop a
 * new identity and rebuild the viewer's `data` (`sameStoredValue`).
 */
const sameField = (a: unknown, b: unknown): boolean => sameStoredValue(a, b);

/**
 * Stamps every structure write sets (`lastModified`, `modifiedBy`) and
 * nothing in the viewer reads. A snapshot that changes only these must not
 * count as a change — neither in the held document (`foldStructureSnapshot`,
 * or the commit's own echo re-publishes the document to every host) nor in
 * the prop (`foldStructureDocIntoAssembled`, or the wrapper rebuilds `data`;
 * a Position nudge on the 3D route measured 2 data rebuilds against main's
 * 1 before this).
 */
const VOLATILE_STAMP_KEYS = new Set([
  'lastModified',
  'modifiedBy',
  'geometryLastModified',
]);

/**
 * Fold a snapshot into the held document.
 *
 * - a field with a write in flight keeps the held value;
 * - a field whose content did not change keeps the held VALUE (identity);
 * - a field the server no longer has is dropped, unless pending;
 * - nothing changed → the held object itself, so a React state set bails out.
 */
export function foldStructureSnapshot(
  held: StructureDoc | null,
  incoming: StructureDoc,
  pending: ReadonlySet<string>
): StructureDoc {
  if (!held) return incoming;
  let changed = false;
  const next: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(held), ...Object.keys(incoming)]);
  for (const key of keys) {
    const heldHas = key in held;
    const inHas = key in incoming;
    const h = (held as Record<string, unknown>)[key];
    const i = (incoming as Record<string, unknown>)[key];
    if (pending.has(key)) {
      if (heldHas) next[key] = h;
      continue;
    }
    // A stamp moves on every write and nobody reads it: take it quietly.
    if (VOLATILE_STAMP_KEYS.has(key)) {
      if (inHas) next[key] = i;
      else if (heldHas) next[key] = h;
      continue;
    }
    if (!inHas) {
      changed = true; // dropped on the server
      continue;
    }
    if (heldHas && sameField(h, i)) {
      next[key] = h;
      continue;
    }
    next[key] = i;
    changed = true;
  }
  return changed ? (next as StructureDoc) : held;
}

/** The keys of the assembled prop that are ROOM-flattened views, never the document's. */
const ROOM_FLATTENED_KEYS = new Set([
  'rooms',
  'walls',
  'floors',
  'objects',
  'doors',
  'windows',
  'openings',
]);

/** Document array → the canonical key it lives under on the assembled prop. */
const CANONICAL_KEY: Record<StructureArrayKey, string> = {
  walls: 'structureWalls',
  doors: 'structureDoors',
  windows: 'structureWindows',
  openings: 'structureOpenings',
};

/**
 * Fold a live structure document into the ASSEMBLED prop
 * (`assembleStructureWithRooms`'s shape): the document's four arrays land
 * under their `structure*` names, every other document field is copied, and
 * the room-flattened keys are never touched — rooms are the room store's.
 * Identity-preserving per field; the prop itself when nothing changed.
 */
export function foldStructureDocIntoAssembled<
  T extends Record<string, unknown>,
>(prev: T | null, doc: StructureDoc): T {
  if (!prev) return doc as unknown as T;
  let changed = false;
  const next: Record<string, unknown> = { ...prev };
  const source = doc as Record<string, unknown>;
  for (const key of Object.keys(source)) {
    if (VOLATILE_STAMP_KEYS.has(key)) continue;
    const target = ROOM_FLATTENED_KEYS.has(key)
      ? STRUCTURE_ARRAY_KEYS.includes(key as StructureArrayKey)
        ? CANONICAL_KEY[key as StructureArrayKey]
        : null
      : key;
    if (!target) continue;
    if (sameField(prev[target], source[key])) continue;
    next[target] = source[key];
    changed = true;
  }
  return changed ? (next as T) : prev;
}

export interface StructureSession {
  readonly id: string;
  /** The document as held now, or null before the first snapshot. */
  current(): StructureDoc | null;
  /** Fires with every CHANGED document (never a no-op snapshot). */
  subscribe(listener: (doc: StructureDoc) => void): () => void;
  /** State an operation set; optimistic locally, transactional on the server. */
  applyOps(ops: StructureOpsByKey): Promise<ApplyOpsResult>;
  /**
   * State a write — element operations and/or a field patch (`exterior`, the
   * user-wall ledger, a scalar), in ONE transaction, with the same
   * optimistic apply and pending-field semantics per top-level key.
   */
  applyWrite(write: StructureWrite): Promise<ApplyOpsResult>;
  /** Re-read from the server, past the cache, honouring pending fields. */
  refresh(): Promise<StructureDoc | null>;
  close(): void;
}

/**
 * Open sessions, by structure id. ONE listener per document however many
 * hosts open it: `openStructure` hands out handles over a shared core and the
 * core closes when the last handle does. The registry is also what lets a
 * write made anywhere in the app — `applyStructureOps` below, which the
 * service's legacy `updateStructureOpeningOps` delegates to — go through the
 * open session and so carry pending-field semantics, without threading the
 * session through six prop hops to every call site.
 */
const openSessions = new Map<
  string,
  { core: StructureSession; refs: number }
>();

/** For tests: forget every open session (listeners are closed). */
export const __closeAllForTests = () => {
  for (const { core } of openSessions.values()) core.close();
  openSessions.clear();
};

export function openStructure(
  structureId: string,
  onError: (error: Error) => void = (e) =>
    console.error(`[structures] ${structureId}:`, e)
): StructureSession {
  const existing = openSessions.get(structureId);
  if (existing) {
    existing.refs += 1;
    return handleOver(structureId, existing.core);
  }
  const core = openCore(structureId, onError);
  openSessions.set(structureId, { core, refs: 1 });
  return handleOver(structureId, core);
}

/** A per-holder handle: `close()` releases this holder's reference only. */
function handleOver(
  structureId: string,
  core: StructureSession
): StructureSession {
  let closed = false;
  return {
    id: structureId,
    current: () => core.current(),
    subscribe: (listener) => core.subscribe(listener),
    applyOps: (ops) => core.applyOps(ops),
    applyWrite: (write) => core.applyWrite(write),
    refresh: () => core.refresh(),
    close() {
      if (closed) return;
      closed = true;
      const entry = openSessions.get(structureId);
      if (!entry || entry.core !== core) return;
      entry.refs -= 1;
      if (entry.refs <= 0) {
        openSessions.delete(structureId);
        core.close();
      }
    },
  };
}

/**
 * State an operation set on a structure from ANYWHERE: through the open
 * session when one holds the document (optimistic, pending fields marked),
 * straight to the adapter's transaction when none does. The one write door
 * for structure arrays; `services/structureService.updateStructureOpeningOps`
 * is its legacy name.
 */
export function applyStructureOps(
  structureId: string,
  ops: StructureOpsByKey
): Promise<ApplyOpsResult> {
  return applyStructureWrite(structureId, { ops });
}

/** A write — ops and/or a field patch — from anywhere, the same way. */
export function applyStructureWrite(
  structureId: string,
  write: StructureWrite
): Promise<ApplyOpsResult> {
  const entry = openSessions.get(structureId);
  return entry
    ? entry.core.applyWrite(write)
    : applyStructureWriteOnServer(structureId, write);
}

function openCore(
  structureId: string,
  onError: (error: Error) => void
): StructureSession {
  let held: StructureDoc | null = null;
  // The newest document the SERVER sent, before any fold: what a field goes
  // back to when a write on it fails, and what a snapshot dropped for a
  // pending field said.
  let latestServer: StructureDoc | null = null;
  const pendingCounts = new Map<string, number>();
  const pendingKeys = (): ReadonlySet<string> =>
    new Set([...pendingCounts.keys()]);
  const listeners = new Set<(doc: StructureDoc) => void>();

  const publish = (next: StructureDoc) => {
    if (next === held) return;
    held = next;
    listeners.forEach((l) => l(next));
  };

  const unwatch = watchStructure(
    structureId,
    ({ doc }) => {
      latestServer = doc;
      publish(foldStructureSnapshot(held, doc, pendingKeys()));
    },
    onError
  );

  const markPending = (keys: string[], delta: 1 | -1) => {
    for (const key of keys) {
      const n = (pendingCounts.get(key) || 0) + delta;
      if (n > 0) pendingCounts.set(key, n);
      else pendingCounts.delete(key);
    }
  };

  /**
   * A write on `keys` has finished. For each key that is no longer pending
   * (no OTHER write still in flight on it), take `valueFor(key)` as the
   * field's value — the server's, which the fold was told to ignore while
   * the field was pending. The commit's own snapshot races the promise and
   * nothing re-sends it, so without this a skipped upsert or a concurrent
   * remote edit to the same array stayed invisible until an unrelated write;
   * and a REJECTED write left the optimistic value on screen for good.
   */
  const settle = (keys: string[], valueFor: (key: string) => unknown) => {
    if (!held) return;
    const next: Record<string, unknown> = { ...held };
    let changed = false;
    for (const key of keys) {
      if (pendingCounts.has(key)) continue;
      const value = valueFor(key);
      if (value === undefined) continue;
      if (sameField((held as Record<string, unknown>)[key], value)) continue;
      next[key] = value;
      changed = true;
    }
    if (changed) publish(next as StructureDoc);
  };
  const serverValue = (key: string): unknown =>
    latestServer ? (latestServer as Record<string, unknown>)[key] : undefined;

  /**
   * What a top-level `key` becomes once a write has committed, from what the
   * transaction wrote: the key's own value when it wrote one; else its dotted
   * entries (`exterior.walls`) merged over the server's last value of the key
   * — the same merge `tx.update` made, and idempotent if the commit's own
   * snapshot already carried it.
   */
  const writtenValue =
    (written: Record<string, unknown>) =>
    (key: string): unknown => {
      if (key in written) return written[key];
      const dotted = Object.entries(written).filter(([path]) =>
        path.startsWith(`${key}.`)
      );
      if (!dotted.length) return undefined;
      let base = (serverValue(key) ??
        (held ? (held as Record<string, unknown>)[key] : undefined) ??
        {}) as Record<string, unknown>;
      for (const [path, value] of dotted) {
        base = setPath(base, path.slice(key.length + 1), value);
      }
      return base;
    };

  // One transaction at a time on this document; op sets that arrive while
  // one is in flight go to the server together, composed in order by the
  // adapter. A burst of Position-arrow presses used to be a burst of
  // transactions contending with each other, each retry re-reading the
  // whole document (`documentWriteQueue.ts`).
  const queue = createWriteQueue<StructureWrite, ApplyOpsResult>((batch) =>
    applyStructureWriteOnServer(structureId, batch)
  );

  return {
    id: structureId,
    current: () => held,
    subscribe(listener) {
      listeners.add(listener);
      if (held) listener(held);
      return () => {
        listeners.delete(listener);
      };
    },
    applyOps(ops) {
      return this.applyWrite({ ops });
    },
    async applyWrite(write) {
      const keys = writeKeys(write);
      if (!keys.length) return { skipped: [] };
      // Optimistic: the same op applier and the same patch the transaction
      // runs, on the held copy, so the screen shows the edit before the
      // round trip.
      if (held) {
        let next: Record<string, unknown> = { ...held };
        for (const [key, ops] of Object.entries(write.ops || {})) {
          if (!ops) continue;
          next[key] = applyOpeningOps(
            (held as Record<string, unknown>)[key] as unknown[],
            ops
          ).array;
        }
        if (write.patch) next = resolvePatch(write.patch, next).doc;
        publish(next as StructureDoc);
      }
      markPending(keys, 1);
      let result: ApplyOpsResult;
      try {
        result = await queue.enqueue(write);
      } catch (error) {
        // Withdrawn: the field goes back to what the server last said.
        markPending(keys, -1);
        settle(keys, serverValue);
        throw error;
      }
      markPending(keys, -1);
      // Committed: the field becomes what the transaction wrote. A writer
      // that reports nothing leaves the fold to the next snapshot, as
      // before. For a batch, every call it carried settles in turn; a key
      // another call of the same batch still holds is settled by that call.
      const written = result.written;
      if (written) settle(keys, writtenValue(written));
      return result;
    },
    async refresh() {
      const fresh = await readStructure(structureId, { fromServer: true });
      if (fresh) publish(foldStructureSnapshot(held, fresh, pendingKeys()));
      return held;
    },
    close() {
      unwatch();
      listeners.clear();
    },
  };
}
